import React, { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';

const GET_DEPENDENCIES = gql`
  query GetDependencies($contextName: String!) {
    dependencies(contextName: $contextName) {
      targetId
      targetName
      targetVersion
      versionRange
      type
      compatible
    }
  }
`;

const GET_AFFECTED_CONTEXTS = gql`
  query GetAffectedContexts($contextName: String!) {
    affectedContexts(contextName: $contextName) {
      id
      name
      version
      distance
      path
    }
  }
`;

const CREATE_DEPENDENCY = gql`
  mutation CreateDependency(
    $sourceName: String!
    $targetName: String!
    $versionRange: String!
    $type: String
  ) {
    createDependency(
      sourceName: $sourceName
      targetName: $targetName
      versionRange: $versionRange
      type: $type
    )
  }
`;

interface Props {
  contextName: string;
}

export function DependencyManager({ contextName }: Props) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [targetName, setTargetName] = useState('');
  const [versionRange, setVersionRange] = useState('^1.0.0');
  const [depType, setDepType] = useState<'requires' | 'conflicts'>('requires');

  const { data: depsData, loading: depsLoading, refetch: refetchDeps } = useQuery(
    GET_DEPENDENCIES,
    { variables: { contextName } }
  );

  const { data: affectedData, loading: affectedLoading } = useQuery(
    GET_AFFECTED_CONTEXTS,
    { variables: { contextName } }
  );

  const [createDependency, { loading: creating }] = useMutation(CREATE_DEPENDENCY, {
    onCompleted: () => {
      setShowAddForm(false);
      setTargetName('');
      setVersionRange('^1.0.0');
      refetchDeps();
      alert('Dependency created successfully!');
    },
    onError: (error) => {
      alert(`Error: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createDependency({
      variables: {
        sourceName: contextName,
        targetName,
        versionRange,
        type: depType,
      },
    });
  };

  if (depsLoading || affectedLoading) return <div className="p-4">Loading dependencies...</div>;

  const dependencies = depsData?.dependencies || [];
  const affectedContexts = affectedData?.affectedContexts || [];

  return (
    <div className="space-y-6">
      {/* Dependencies Section */}
      <div className="border rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Dependencies</h3>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="text-sm bg-primary text-white px-3 py-1 rounded hover:bg-primary/90"
          >
            {showAddForm ? 'Cancel' : '+ Add Dependency'}
          </button>
        </div>

        {showAddForm && (
          <form onSubmit={handleSubmit} className="mb-4 p-3 bg-gray-50 rounded border">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium mb-1">Target Context</label>
                <input
                  type="text"
                  value={targetName}
                  onChange={(e) => setTargetName(e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                  placeholder="context_name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Version Range</label>
                <input
                  type="text"
                  value={versionRange}
                  onChange={(e) => setVersionRange(e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                  placeholder="^1.0.0"
                  required
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium mb-1">Type</label>
              <select
                value={depType}
                onChange={(e) => setDepType(e.target.value as any)}
                className="w-full border rounded px-2 py-1 text-sm"
              >
                <option value="requires">Requires (DEPENDS_ON)</option>
                <option value="conflicts">Conflicts With</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={creating}
              className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Dependency'}
            </button>
          </form>
        )}

        {dependencies.length === 0 ? (
          <p className="text-sm text-gray-500">No dependencies</p>
        ) : (
          <div className="space-y-2">
            {dependencies.map((dep: any, i: number) => (
              <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                <div>
                  <span className="font-medium">{dep.targetName}</span>
                  <span className="text-gray-500 ml-2">v{dep.targetVersion}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{dep.versionRange}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    dep.type === 'requires' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {dep.type}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    dep.compatible ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {dep.compatible ? '✓ Compatible' : '⚠ Incompatible'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Affected Contexts Section */}
      <div className="border rounded-lg p-4">
        <h3 className="text-lg font-bold mb-4">
          Affected Contexts
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({affectedContexts.length} contexts depend on this)
          </span>
        </h3>
        {affectedContexts.length === 0 ? (
          <p className="text-sm text-gray-500">No contexts depend on this one</p>
        ) : (
          <div className="space-y-2">
            {affectedContexts.map((ctx: any) => (
              <div key={ctx.id} className="flex items-center justify-between p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                <div>
                  <span className="font-medium">{ctx.name}</span>
                  <span className="text-gray-500 ml-2">v{ctx.version}</span>
                </div>
                <div className="text-xs text-gray-500">
                  Distance: {ctx.distance}
                </div>
              </div>
            ))}
          </div>
        )}
        {affectedContexts.length > 0 && (
          <p className="text-xs text-yellow-700 mt-3">
            ⚠️ Updating this context may affect {affectedContexts.length} other context{affectedContexts.length > 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
}
